import { useEffect, useState, useRef, useCallback } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Palette, Upload, Trash, ShieldWarning, Info } from "@phosphor-icons/react";

const MODE_LABEL = {
  claroshield: "ClaroShield (default)",
  brandless: "Brandless (no logo)",
  custom: "Custom (customer logo)",
  confidential: "Confidential (custom logo + confidentiality markings)",
};
const MODE_HELP = {
  claroshield: "All documents carry the ClaroShield logo. Used for demo / trial / no-plan tenants.",
  brandless: "No logo in headers or footers. Plain document.",
  custom: "Your uploaded logo replaces the ClaroShield mark on every report.",
  confidential: "Your logo (or brandless) plus configurable CONFIDENTIAL markings on every page.",
};

export default function BrandingSettings() {
  const [state, setState] = useState(null);
  const [saving, setSaving] = useState(false);
  const [logoNonce, setLogoNonce] = useState(0);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/branding");
      setState(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (!state) return <div className="p-6 text-sm text-muted-foreground" data-testid="branding-loading">Loading branding…</div>;

  const { is_staff, mode, mode_override, company_name, confidentiality, active_plan, has_logo } = state;
  const canEditCustomerFields = is_staff || mode_override === "custom" || mode_override === "confidential";

  const setLocal = (patch) => setState((s) => ({ ...s, ...patch }));
  const setConf = (patch) => setState((s) => ({ ...s, confidentiality: { ...s.confidentiality, ...patch } }));

  const save = async () => {
    setSaving(true);
    try {
      const body = { confidentiality };
      if (is_staff) body.mode_override = mode_override || null;
      if (canEditCustomerFields) body.company_name = company_name;
      const { data } = await api.put("/admin/branding", body);
      setState(data);
      toast.success("Branding saved.");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const uploadLogo = async (file) => {
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) return toast.error("Logo must be under 2 MB.");
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post("/admin/branding/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Logo uploaded.");
      setLogoNonce((n) => n + 1);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const removeLogo = async () => {
    if (!window.confirm("Remove your custom logo? Reports will fall back to the ClaroShield logo until a new one is uploaded.")) return;
    try {
      await api.delete("/admin/branding/logo");
      toast.success("Logo removed.");
      setLogoNonce((n) => n + 1);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const backend = process.env.REACT_APP_BACKEND_URL;

  return (
    <div className="p-6 space-y-6 max-w-5xl" data-testid="branding-settings">
      <div className="flex items-start gap-3">
        <Palette size={32} weight="duotone" />
        <div className="flex-1">
          <h2 className="text-xl font-bold">Report & Document Branding</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Controls the logo, header, footer, and watermark of every generated Word/PDF/Excel report. Effective mode is
            <strong className="mx-1 capitalize">{mode}</strong>
            (plan: <span className="font-mono text-xs">{active_plan || "—"}</span>).
          </p>
        </div>
      </div>

      {/* ---- Master switch (staff only) ---------------------------------- */}
      <section className="border border-border p-5 bg-card">
        <div className="flex items-center gap-2 mb-3">
          <ShieldWarning size={18} weight="duotone" className={is_staff ? "text-primary" : "text-muted-foreground"} />
          <div>
            <div className="font-semibold">Branding mode {is_staff ? "" : <span className="text-xs text-muted-foreground">(ClaroShield staff only)</span>}</div>
            <div className="text-xs text-muted-foreground">
              {is_staff
                ? "Master switch — override the auto-detected mode. Blank = auto (plan-driven)."
                : "Contact ClaroShield to switch modes. Auto-detected from your subscription."}
            </div>
          </div>
        </div>
        <div className="grid gap-2">
          {["", ...state.modes].map((m) => (
            <label key={m || "auto"} className={`flex items-start gap-3 border rounded p-3 cursor-pointer ${((mode_override || "") === m) ? "border-primary bg-primary/5" : "border-border"}`}>
              <input
                type="radio"
                name="branding-mode"
                data-testid={`branding-mode-${m || "auto"}`}
                checked={(mode_override || "") === m}
                onChange={() => setLocal({ mode_override: m })}
                disabled={!is_staff}
                className="mt-1"
              />
              <div>
                <div className="text-sm font-medium">
                  {m ? MODE_LABEL[m] : <span>Auto <span className="text-muted-foreground font-normal">(plan-driven default)</span></span>}
                </div>
                {m && <div className="text-xs text-muted-foreground mt-0.5">{MODE_HELP[m]}</div>}
              </div>
            </label>
          ))}
        </div>
      </section>

      {/* ---- Customer branding fields ----------------------------------- */}
      <section className={`border border-border p-5 bg-card ${canEditCustomerFields ? "" : "opacity-70"}`}>
        <div className="font-semibold mb-1">Company logo & name</div>
        <div className="text-xs text-muted-foreground mb-3">
          {canEditCustomerFields
            ? "PNG or JPEG under 2 MB. Appears at the top of every generated Word/PDF report."
            : "Locked. Ask ClaroShield to switch the mode to Custom or Confidential to enable your logo."}
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <Label className="text-xs">Company name (for confidentiality banners)</Label>
            <Input
              data-testid="branding-company-name"
              value={company_name || ""}
              onChange={(e) => setLocal({ company_name: e.target.value })}
              disabled={!canEditCustomerFields}
              placeholder="Sunset Solar"
              className="mt-1"
            />
          </div>
          <div>
            <Label className="text-xs">Current logo</Label>
            <div className="mt-1 border border-border p-3 bg-secondary/30 flex items-center gap-3">
              <img
                src={`${backend}/api/admin/branding/logo?nonce=${logoNonce}`}
                alt="branding logo"
                className="h-10 object-contain bg-white p-1 border border-border"
                onError={(e) => { e.currentTarget.style.display = "none"; }}
                data-testid="branding-logo-preview"
              />
              <div className="text-xs">
                {has_logo ? <span className="text-green-700 font-medium">Custom logo uploaded</span>
                          : <span className="text-muted-foreground">Using ClaroShield logo (no custom logo uploaded)</span>}
              </div>
              <div className="ml-auto flex gap-1">
                <input ref={fileRef} type="file" accept="image/png,image/jpeg" className="hidden"
                  onChange={(e) => uploadLogo(e.target.files?.[0])} data-testid="branding-logo-upload-input" />
                <Button size="sm" variant="outline" disabled={!canEditCustomerFields}
                  onClick={() => fileRef.current?.click()} data-testid="branding-logo-upload-btn">
                  <Upload size={14} className="mr-1" /> Upload
                </Button>
                {has_logo && <Button size="sm" variant="outline" disabled={!canEditCustomerFields}
                  onClick={removeLogo} data-testid="branding-logo-remove-btn">
                  <Trash size={14} className="mr-1" /> Remove
                </Button>}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ---- Confidentiality markings ----------------------------------- */}
      <section className={`border border-border p-5 bg-card ${canEditCustomerFields ? "" : "opacity-70"}`}>
        <div className="font-semibold mb-1">Confidentiality markings</div>
        <div className="text-xs text-muted-foreground mb-3">
          Add banners and/or a watermark to every generated report. Independent toggles for header, footer, and watermark.
        </div>
        <label className="inline-flex items-center gap-2 text-sm mb-3">
          <input type="checkbox" data-testid="branding-conf-enabled"
            checked={!!confidentiality.enabled}
            onChange={(e) => setConf({ enabled: e.target.checked })}
            disabled={!canEditCustomerFields} />
          <span className="font-medium">Enable confidentiality markings</span>
        </label>
        <div className={`space-y-3 ${confidentiality.enabled ? "" : "opacity-60 pointer-events-none"}`}>
          <div>
            <Label className="text-xs">Banner text (use <code className="text-[11px]">{"{company}"}</code> to substitute the company name)</Label>
            <Textarea
              data-testid="branding-conf-text"
              value={confidentiality.text || ""}
              onChange={(e) => setConf({ text: e.target.value })}
              rows={2}
              placeholder="CONFIDENTIAL — For {company} authorized personnel and auditor use only."
              className="mt-1"
              disabled={!canEditCustomerFields}
            />
          </div>
          <div className="grid grid-cols-3 gap-3">
            {[
              { k: "header", label: "Header banner", help: "Above document title" },
              { k: "footer", label: "Footer banner", help: "Bottom of every page" },
              { k: "watermark", label: "Diagonal watermark", help: "Large grey text on every page" },
            ].map((opt) => (
              <label key={opt.k} className="flex items-start gap-2 border border-border p-3 rounded cursor-pointer">
                <input type="checkbox"
                  data-testid={`branding-conf-${opt.k}`}
                  checked={!!confidentiality[opt.k]}
                  onChange={(e) => setConf({ [opt.k]: e.target.checked })}
                  disabled={!canEditCustomerFields}
                  className="mt-0.5" />
                <div>
                  <div className="text-sm font-medium">{opt.label}</div>
                  <div className="text-[11px] text-muted-foreground">{opt.help}</div>
                </div>
              </label>
            ))}
          </div>
        </div>
      </section>

      <div className="flex items-center gap-2 border-t border-border pt-4">
        <Button onClick={save} disabled={saving} data-testid="branding-save-btn">
          {saving ? "Saving…" : "Save branding"}
        </Button>
        <Button variant="outline" onClick={load} data-testid="branding-reload-btn">Reload</Button>
        <div className="ml-auto flex items-center gap-2 text-xs text-muted-foreground">
          <Info size={14} />
          Currently applies to the CIP-010 R1.1 baseline document. Other exports roll out in the next release.
        </div>
      </div>
    </div>
  );
}
